import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, resolveImage } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { COMPANY } from "@/i18n/translations";
import { calcMonthlyRate, fmtEUR } from "@/lib/finance";
import { Calendar, Gauge, Zap, Fuel, ArrowRight, MessageCircle, Flame } from "lucide-react";

const fmtNum = (n) => new Intl.NumberFormat("de-DE").format(n);

export default function DealOfDay() {
  const { t } = useLang();
  const [vehicle, setVehicle] = useState(undefined);

  useEffect(() => {
    api
      .get("/vehicles", { params: { featured: true, status: "available", limit: 1 } })
      .then(({ data }) => setVehicle(Array.isArray(data) && data.length ? data[0] : null))
      .catch(() => setVehicle(null));
  }, []);

  if (!vehicle) return null;

  const img = vehicle.images?.length ? resolveImage(vehicle.images[0]) : "/vehicle-placeholder.jpg";
  const fin = calcMonthlyRate(vehicle.price, {
    down: vehicle.finance_down,
    term: vehicle.finance_term,
    balloon: vehicle.finance_balloon,
    annualRate: vehicle.finance_rate,
  });
  const waText = `Hallo DNA Automotive, ich interessiere mich für das Angebot des Tages: ${vehicle.brand} ${vehicle.model} (${fmtEUR(vehicle.price)}).`;
  const specs = [
    { icon: Calendar, value: vehicle.year },
    { icon: Gauge, value: `${fmtNum(vehicle.mileage)} km` },
    { icon: Zap, value: `${vehicle.power_hp} PS` },
    { icon: Fuel, value: vehicle.fuel },
  ];

  return (
    <section className="py-24 md:py-32 px-6 md:px-10 bg-obsidian border-t border-white/10" data-testid="deal-of-the-day-section">
      <div className="max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-10"
        >
          <Flame className="w-4 h-4 text-brand-bright" />
          <p className="text-brand-bright text-xs uppercase tracking-[0.35em] font-bold">{t.deal.overline}</p>
          <span className="hidden md:block h-px flex-1 bg-gradient-to-r from-brand-light/40 to-transparent" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-8 group relative overflow-hidden border border-white/10"
          >
            <Link to={`/fahrzeug/${vehicle.id}`} data-testid="deal-image-link">
              <div className="relative aspect-[16/10] lg:aspect-auto lg:h-full lg:min-h-[520px] overflow-hidden">
                <img
                  src={img}
                  alt={`${vehicle.brand} ${vehicle.model}`}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-obsidian/20" />
                <span className="absolute top-5 left-5 bg-black/60 backdrop-blur-2xl border border-brand-light/30 text-brand-bright text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] px-4 py-2">
                  {t.deal.overline}
                </span>
                <span className="absolute bottom-5 left-5 bg-black/60 backdrop-blur-2xl border border-white/10 text-white text-xs uppercase tracking-[0.2em] px-4 py-2">
                  {vehicle.brand}
                </span>
              </div>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4 bg-obsidian-surface border border-white/10 hover:border-brand-light/30 transition-colors duration-500 p-8 md:p-10 flex flex-col shadow-[0_0_40px_-10px_rgba(46,224,107,0.2)]"
          >
            <h3 className="font-heading font-black uppercase tracking-tight text-white text-2xl md:text-3xl leading-[1.05]" data-testid="deal-title">
              {vehicle.model}
            </h3>

            <ul className="grid grid-cols-2 gap-x-4 gap-y-3 mt-7 text-sm text-zinc-300">
              {specs.map(({ icon: Icon, value }) => (
                <li key={value} className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-brand-bright shrink-0" />
                  <span className="uppercase tracking-wider text-xs md:text-sm">{value}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="font-heading text-4xl md:text-5xl font-extrabold text-brand-bright leading-none" data-testid="deal-price">
                {fmtEUR(vehicle.price)}
              </p>
              <p className="text-sm text-zinc-400 mt-3">
                {t.deal.financing} <span className="text-white font-bold">{fmtEUR(fin.rate)} {t.deal.perMonth}</span>
                <span className="text-zinc-500">*</span>
              </p>
            </div>

            <div className="mt-auto pt-8 space-y-3">
              <Link
                to={`/fahrzeug/${vehicle.id}`}
                data-testid="deal-cta-button"
                className="group flex items-center justify-center gap-2 w-full bg-brand text-black font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-brand-hover transition-colors"
              >
                {t.deal.cta}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(waText)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="deal-whatsapp-button"
                className="flex items-center justify-center gap-2 w-full border border-white/20 text-white font-bold uppercase tracking-wider text-sm px-8 py-4 hover:border-brand-light hover:text-brand-bright transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                {t.deal.whatsapp}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
